// noinspection DuplicatedCode
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";

import { Colors } from "../../assets/Colors";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../components/form/HrdInputGenerator";
import { useForm } from "../../components/isRequired";
import { useMutation } from "@tanstack/react-query";
import { fetchGet, fetchPost } from "../../fetchers";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Skeltons from "../../components/loading/Skeltons";
import { SearchBarSmallV2 } from "../../components/form/SearchBar";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 10;

// "YYYY-MM-DD" (tanggal murni dari server) -> "DD/MM/YYYY" tanpa konversi zona waktu.
const fmtTanggalPO = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "-");

const MonitoringGudang = () => {
  const navigate = useNavigate();
  const initialState = { Bulan: new Date(), BisnisUnitID: "U0002" };
  const { values, errors, touched, customHandler } = useForm(initialState, []);

  const [page, setPage]               = useState(1);
  const [totalPage, setTotalPage]     = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [param, setParam]             = useState({
    BisnisUnitID: "U0002",
    Bulan: (new Date().getMonth() + 1).toString(),
    Tahun: new Date().getFullYear().toString(),
  });

  const mutationData = useMutation((url) => fetchGet(url));
  const KirimTrans   = useMutation((formData) =>
    fetchPost("Procurement/GoodsAcceptance/approval", formData)
  );

  const header = [
    { name: "Goods Acceptance ID", key: "GoodsAcceptanceID" },
    { name: "Purchase Order ID",   key: "PurchaseOrderID" },
    { name: "Tanggal PO",         key: "TanggalPO" },
    { name: "Nama Penerima",       key: "NamaPenerima" },
    { name: "Keterangan",          key: "Keterangan" },
    { name: "Tanggal",             key: "Tanggal" },
    { name: "Status",              key: "Status" },
  ];

  // ── Load data ─────────────────────────────────────────────
  useEffect(() => {
    if (!param) return;
    const url = `Procurement/GoodsAcceptance/all/monitoring/${param.BisnisUnitID}/${param.Bulan}?tahun=${param.Tahun}`;
    mutationData.mutate(url);
    setPage(1);
    setSearchInput("");
    setSearchQuery("");
  }, [param]);

  // Reload setelah kirim
  useEffect(() => {
    if (KirimTrans.isSuccess && param) {
      const url = `Procurement/GoodsAcceptance/all/monitoring/${param.BisnisUnitID}/${param.Bulan}?tahun=${param.Tahun}`;
      mutationData.mutate(url);
    }
  }, [KirimTrans.status]);

  // ── Client-side filter ─────────────────────────────────────
  const allRows = mutationData.data?.data || [];

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return allRows;
    const kw = searchQuery.toLowerCase();
    return allRows.filter((r) =>
      [r.GoodsAcceptanceID, r.PurchaseOrderID, r.NamaPenerima, r.Keterangan, r.Status]
        .some((v) => v && v.toLowerCase().includes(kw))
    );
  }, [allRows, searchQuery]);

  // ── Pagination ─────────────────────────────────────────────
  useEffect(() => {
    setTotalPage(Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)));
    setPage(1);
  }, [filtered.length]);

  const safePage = Math.min(page, totalPage);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  // ── Search handler ─────────────────────────────────────────
  const handleSearchChange = useCallback((e) => {
    const val = typeof e === "string" ? e : e?.target?.value || "";
    setSearchInput(val);
    setSearchQuery(val);
    setPage(1);
  }, []);

  // ── Filter button ──────────────────────────────────────────
  const filterApi = useCallback(() => {
    setParam({
      BisnisUnitID: values.BisnisUnitID,
      Bulan: (new Date(values.Bulan).getMonth() + 1).toString(),
      Tahun: new Date(values.Bulan).getFullYear().toString(),
    });
  }, [values]);

  // ── Pagination ─────────────────────────────────────────────
  const prevPage = () => { if (page > 1) setPage((p) => p - 1); };
  const nextPage = () => { if (page < totalPage) setPage((p) => p + 1); };

  // ── Kirim ──────────────────────────────────────────────────
  const kirim = (id) => {
    KirimTrans.mutate({ GoodsAcceptanceID: id.replaceAll("-", "/") });
  };

  // ── Action options per status ──────────────────────────────
  const getActionOptions = (status) => {
    if (status === "Approved" || status === "Selesai") {
      return [{ label: "Detail", value: "1" }, { label: "Blanko", value: "2" }];
    }
    if (status === "Menunggu Approval") {
      return [{ label: "Detail", value: "1" }];
    }
    return [
      { label: "Detail", value: "1" },
      { label: "Edit",   value: "4" },
      { label: "Kirim",  value: "3" },
    ];
  };

  const handleAction = (text, row) => {
    const id = row.GoodsAcceptanceID.replaceAll("/", "-");
    const bunit = param?.BisnisUnitID || values.BisnisUnitID;
    if (text.value === "1") navigate(`/gudang/transaction/detail-penerimaan-barang/${bunit}/${id}`);
    else if (text.value === "2") navigate(`/gudang/blanco/${id}`);
    else if (text.value === "3") kirim(id);
    else if (text.value === "4") navigate(`/gudang/transaction/form-penerimaan-barang/${bunit}/${id}`);
  };

  // ── Export Excel ───────────────────────────────────────────
  // Nilai satu sel untuk export (Excel/PDF), termasuk keterangan tambahan
  // "Barang sudah diterima semua" pada kolom Keterangan bila PO sudah lengkap diterima.
  const cellValue = (row, h) => {
    if (h.key === "Tanggal" && row[h.key])
      return new Date(row[h.key]).toLocaleDateString("ID");
    if (h.key === "TanggalPO") return fmtTanggalPO(row[h.key]);
    if (h.key === "Keterangan") {
      const base = row[h.key] || "";
      return row.SudahDiterimaSemua
        ? (base ? `${base} — Barang sudah diterima semua` : "Barang sudah diterima semua")
        : base;
    }
    return row[h.key] || "";
  };

  function handleDownloadExcel() {
    const names = header.map((h) => h.name);
    const body  = filtered.map((row) => header.map((h) => cellValue(row, h)));
    downloadExcel({ fileName: "Monitoring_Penerimaan", sheet: "data", tablePayload: { header: names, body } });
  }

  // ── Export PDF ─────────────────────────────────────────────
  function handleDownloadPDF() {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(12);
    doc.text("Monitoring Penerimaan Barang", 14, 14);
    autoTable(doc, {
      head: [header.map((h) => h.name)],
      body: filtered.map((row) => header.map((h) => cellValue(row, h))),
      startY: 20,
      styles: { fontSize: 7 },
    });
    doc.save("Monitoring_Penerimaan.pdf");
  }

  const hasData = !!mutationData.data;

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <div className="md:ml-64">
        <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
          <div className="container mx-auto max-w-full" />
        </div>

        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card>

                {/* ── Filter row ── */}
                <div className="flex flex-row flex-wrap items-start justify-start w-full px-2 py-2">
                  <div className="w-1/3">
                    <HrdInputGenerator
                      type={"BisnisUnit"} name={"BisnisUnitID"} title={"BisnisUnit"}
                      value={values} required={true} onChange={customHandler}
                      touched={touched} errors={errors} size={"full"}
                    />
                  </div>
                  <div className="w-1/3">
                    <HrdInputGenerator
                      type={"bulan"} name={"Bulan"} title={"Bulan"}
                      value={values} required={true} onChange={customHandler}
                      touched={touched} errors={errors} size={"full"}
                    />
                  </div>
                  <div className="w-1/3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Buka data
                    </label>
                    <button
                      type="button"
                      onClick={filterApi}
                      disabled={mutationData.isLoading}
                      className="text-main-100 hover:text-white border border-main-100 hover:bg-main-100 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-7 py-2 text-center mr-2 mb-2"
                    >
                      {mutationData.isLoading ? "Loading..." : "Buka"}
                    </button>
                  </div>
                </div>

                {/* ── Konten ── */}
                <div className="flex flex-col content-center">
                  {mutationData.isLoading ? (
                    <div className="w-full pt-0 items-left">
                      <Skeltons />
                    </div>
                  ) : hasData ? (
                    <>
                      {/* Download + Search row */}
                      <div className="flex w-full justify-between px-4 pt-2 flex-col sm:flex-row mt-2">
                        <div className="flex flex-row gap-2">
                          <button
                            onClick={handleDownloadExcel}
                            className="flex flex-row items-center justify-center my-2 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow"
                          >
                            Download Excel
                          </button>
                          <button
                            onClick={handleDownloadPDF}
                            className="flex flex-row items-center justify-center my-2 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow"
                          >
                            Download PDF
                          </button>
                        </div>
                        <div className="flex flex-row w-96">
                          <SearchBarSmallV2
                            text={"Cari Nama"}
                            value={searchInput}
                            funcs={handleSearchChange}
                          />
                        </div>
                      </div>

                      {/* Tabel */}
                      <div className="overflow-x-auto px-4 pt-2">
                        <table className="table-auto w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
                          <thead className="text-xs text-orange-600 uppercase bg-gray-50 dark:bg-gray-700 dark:text-orange-400">
                            <tr>
                              {header.map((h, i) => (
                                <th key={i} scope="col" className="py-3 px-6">{h.name}</th>
                              ))}
                              <th scope="col" className="py-3 px-6">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageRows.length === 0 ? (
                              <tr>
                                <td colSpan={header.length + 1} className="text-center py-8 text-gray-400">
                                  {searchQuery ? `Tidak ada data untuk "${searchQuery}"` : "Tidak ada data"}
                                </td>
                              </tr>
                            ) : (
                              pageRows.map((row, id) => (
                                <tr
                                  key={row.GoodsAcceptanceID || id}
                                  className={id % 2 === 0
                                    ? "bg-gray-50 border-b hover:bg-gray-200 hover:text-main-100"
                                    : "bg-white border-b hover:bg-gray-200 hover:text-main-100"}
                                >
                                  {header.map((h, i) => (
                                    <td key={i} className="py-4 px-6">
                                      {h.key === "Tanggal" && row[h.key]
                                        ? new Date(row[h.key]).toLocaleDateString("ID")
                                        : h.key === "TanggalPO"
                                        ? fmtTanggalPO(row[h.key])
                                        : h.key === "Status"
                                        ? (
                                          <span className={
                                            row[h.key] === "Selesai" || row[h.key] === "Approved"
                                              ? "text-green-600 font-medium"
                                              : row[h.key] === "Menunggu Approval"
                                              ? "text-yellow-600 font-medium"
                                              : "text-gray-600"
                                          }>
                                            {row[h.key] || "-"}
                                          </span>
                                        )
                                        : h.key === "Keterangan"
                                        ? (
                                          <div className="flex flex-col gap-1">
                                            <span>{row[h.key] || "-"}</span>
                                            {row.SudahDiterimaSemua && (
                                              <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 rounded px-2 py-0.5 text-xs font-medium w-fit">
                                                ✓ Barang sudah diterima semua
                                              </span>
                                            )}
                                          </div>
                                        )
                                        : row[h.key] || "-"}
                                    </td>
                                  ))}
                                  <td className="py-4 px-6 font-medium whitespace-nowrap">
                                    <Select
                                      className="appearance-none block w-full text-gray-700 border border-gray-200 outline-none bg-white border-gray-500 rounded-lg"
                                      onChange={(text) => handleAction(text, row)}
                                      options={getActionOptions(row.Status)}
                                      placeholder="Aksi"
                                      value={null}
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Info filter */}
                      {searchQuery && (
                        <div className="px-4 pt-1 text-xs text-green-600">
                          Menampilkan {filtered.length} dari {allRows.length} data untuk "{searchQuery}"
                        </div>
                      )}

                      {/* Pagination */}
                      <div className="flex flex-row justify-end items-center gap-4 px-4 py-4">
                        <button
                          onClick={prevPage}
                          disabled={safePage <= 1}
                          className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                        >
                          Prev
                        </button>
                        <span className="text-sm">
                          Page {safePage} / {totalPage}
                        </span>
                        <button
                          onClick={nextPage}
                          disabled={safePage >= totalPage}
                          className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                        >
                          Next
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-gray-400 py-10">
                      Tidak ada data untuk bulan ini
                    </p>
                  )}
                </div>

              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonitoringGudang;
